/**
 * Enhanced Step 4: Schema Preview with Knowledge Graph Design
 *
 * This component provides two modes:
 * 1. Quick mode: Generate schema from data mappings (existing behavior)
 * 2. Advanced mode: Use Knowledge Graph Design wizard for enterprise systems
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BarChart3,
  Database,
  GitBranch,
  Key,
  Search,
  Sparkles,
  CheckCircle2,
  Code,
  Settings2,
  LayoutDashboard,
  ChevronRight,
  Eye,
  List,
  ArrowRight,
  Table2,
  Columns,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Card, { CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { useAnalysisStore, SchemaDesign, SchemaNode, SchemaRelationship, SystemDataSource } from '../../stores/analysisStore';
import { useAppStore } from '../../stores/appStore';
import { KnowledgeGraphDesignWizard } from '../../components/knowledge-graph-design';
import SuggestedOntologyView from '../../components/knowledge-graph-design/SuggestedOntologyView';
import { ontologySuggestionApi, type SuggestedSchema } from '../../lib/api/ontologySuggestionApi';
import { SchemaVisualizer } from '../../components/processing/SchemaVisualizer';
import { SchemaGenerationProgress, type SchemaGenerationResult } from '../../components/processing/SchemaGenerationProgress';
import SchemaDDLViewer from '../../components/case-analytics/SchemaDDLViewer';
import { Neo4jSchema } from '../../types';
import { api } from '../../lib/api';
import {
  generateSchemaPreview,
  generateQuickSchema,
  getSavedSchema,
  saveQuickSchema,
  getSelectedTablesForCase,
  type QuickSchemaGenerationRequest,
  type QuickSchemaGenerationResponse,
  type QuickSchemaTable,
  type QuickSchemaTableColumn,
  type SelectedTableInfo,
} from '../../lib/api/knowledgeGraphDesignApi';
import type { SchemaPreview, PreviewTable, PreviewRelationship } from '../../types/knowledgeGraphDesign';

interface Step4Props {
  onNext: () => void;
  onBack: () => void;
}

type DesignMode = 'quick' | 'advanced' | null;

/**
 * Suggested Ontology availability — mirrors the KG-design wizard's gate (and the
 * backend `enable_suggested_schema` flag) so Quick Generation offers the SAME
 * enhanced, company-aware ontology suggestion the Advanced Design flow does.
 */
const SUGGESTED_SCHEMA_FLAG =
  String(import.meta.env.VITE_ENABLE_SUGGESTED_SCHEMA ?? '').toLowerCase() === 'true';

/**
 * Convert an accepted :SuggestedSchema into the analysis store's SchemaDesign shape
 * so the existing Quick preview/save/visualizer path renders it unchanged. Mirrors
 * the saved-schema conversion used when restoring a schema from Neo4j (the store
 * keeps constraints/indexes as Cypher DDL strings, not objects).
 */
const suggestionToStoreSchema = (suggestion: SuggestedSchema): SchemaDesign => {
  const sd = suggestion.schemaDesign;
  return {
    nodes: (sd.nodes || []).map((n) => ({
      label: n.label,
      properties: (n.properties || []).map((p) => ({
        name: p.name,
        type: p.type || 'String',
        required: p.required || false,
        unique: p.unique || false,
        indexed: p.indexed || false,
      })),
    })),
    relationships: (sd.relationships || []).map((r) => ({
      type: r.type,
      from: r.from,
      to: r.to,
      properties: (r.properties || []).map((p) => ({
        name: p.name,
        type: p.type || 'String',
      })),
    })),
    constraints: (sd.constraints || []).map((c: any) =>
      typeof c === 'string'
        ? c
        : `CREATE CONSTRAINT ${c.name} IF NOT EXISTS FOR (n:${c.label}) REQUIRE (${(c.properties || [])
            .map((p: string) => `n.${p}`)
            .join(', ')}) IS ${c.type === 'unique' ? 'UNIQUE' : 'NODE KEY'}`
    ),
    indexes: (sd.indexes || []).map((i: any) =>
      typeof i === 'string'
        ? i
        : `CREATE INDEX ${i.name} IF NOT EXISTS FOR (n:${i.label}) ON (${(i.properties || [])
            .map((p: string) => `n.${p}`)
            .join(', ')})`
    ),
    cypherDDL: suggestion.cypherDdlPreview || '',
  };
};
type ViewMode = 'visual' | 'details' | 'mapping';

// Generate Neo4j schema from data mappings
const generateSchemaFromMappings = (mappings: any[]): SchemaDesign => {
  const uniqueEntities = new Set<string>();
  const nodes: SchemaNode[] = [];
  const relationships: SchemaRelationship[] = [];
  const constraints: string[] = [];
  const indexes: string[] = [];

  mappings.forEach(mapping => {
    uniqueEntities.add(mapping.targetEntity);
  });

  Array.from(uniqueEntities).forEach(entity => {
    const relatedMappings = mappings.filter(m => m.targetEntity === entity);
    const allProperties = new Set<string>();

    relatedMappings.forEach(m => {
      m.targetProperties.forEach((prop: string) => allProperties.add(prop));
    });

    const idProp = `${entity.toLowerCase()}Id`;
    allProperties.add(idProp);

    nodes.push({
      label: entity,
      properties: Array.from(allProperties).map((prop, idx) => ({
        name: prop,
        type: prop.includes('date') || prop.includes('Date') ? 'DateTime' :
              prop.includes('amount') || prop.includes('value') ? 'Float' :
              prop.includes('count') || prop.includes('quantity') ? 'Integer' : 'String',
        required: prop === idProp,
        unique: prop === idProp,
        indexed: prop === idProp || idx < 2,
      })),
    });

    constraints.push(`CREATE CONSTRAINT ${entity.toLowerCase()}_id_unique FOR (n:${entity}) REQUIRE n.${idProp} IS UNIQUE`);
    indexes.push(`CREATE INDEX ${entity.toLowerCase()}_id_index FOR (n:${entity}) ON (n.${idProp})`);
  });

  const nodeLabels = nodes.map(n => n.label);
  for (let i = 0; i < nodeLabels.length - 1; i++) {
    const from = nodeLabels[i];
    const to = nodeLabels[i + 1];

    relationships.push({
      type: `HAS_${to.toUpperCase()}`,
      from,
      to,
      properties: [
        { name: 'createdAt', type: 'DateTime' },
        { name: 'updatedAt', type: 'DateTime' },
      ],
    });
  }

  const cypherDDL = generateCypherDDL(nodes, relationships, constraints, indexes);

  return {
    nodes,
    relationships,
    constraints,
    indexes,
    cypherDDL,
  };
};

// Map database column types to Neo4j types
const mapColumnType = (dbType: string): string => {
  const type = dbType.toLowerCase();
  if (type.includes('int') || type.includes('number')) return 'Integer';
  if (type.includes('float') || type.includes('double') || type.includes('decimal')) return 'Float';
  if (type.includes('date') || type.includes('time')) return 'DateTime';
  if (type.includes('bool')) return 'Boolean';
  return 'String';
};

// Convert SchemaPreview from API to SchemaDesign format for the store
const convertSchemaPreviewToDesign = (preview: SchemaPreview): SchemaDesign => {
  const nodes: SchemaNode[] = preview.tables.map(table => {
    // Convert table name to PascalCase for node label
    const label = table.table_name
      .split(/[_\s]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');

    return {
      label,
      properties: table.columns.map((col, idx) => ({
        name: col.column_name,
        type: mapColumnType(col.data_type),
        required: col.is_primary_key,
        unique: col.is_primary_key,
        indexed: col.is_primary_key || col.is_foreign_key || idx < 2,
      })),
    };
  });

  const relationships: SchemaRelationship[] = preview.relationships.map(rel => {
    // Convert source/target table names to PascalCase
    const fromLabel = rel.source_table
      .split(/[_\s]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
    const toLabel = rel.target_table
      .split(/[_\s]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');

    return {
      type: rel.relationship_type,
      from: fromLabel,
      to: toLabel,
      properties: [
        { name: 'createdAt', type: 'DateTime' },
      ],
    };
  });

  // Generate constraints and indexes
  const constraints: string[] = [];
  const indexes: string[] = [];

  nodes.forEach(node => {
    const idProp = node.properties.find(p => p.unique);
    if (idProp) {
      constraints.push(
        `CREATE CONSTRAINT ${node.label.toLowerCase()}_${idProp.name}_unique FOR (n:${node.label}) REQUIRE n.${idProp.name} IS UNIQUE`
      );
    }
    node.properties
      .filter(p => p.indexed)
      .forEach(p => {
        indexes.push(`CREATE INDEX ${node.label.toLowerCase()}_${p.name}_index FOR (n:${node.label}) ON (n.${p.name})`);
      });
  });

  const cypherDDL = generateCypherDDL(nodes, relationships, constraints, indexes);

  return {
    nodes,
    relationships,
    constraints,
    indexes,
    cypherDDL,
  };
};

const generateCypherDDL = (
  nodes: SchemaNode[],
  relationships: SchemaRelationship[],
  constraints: string[],
  indexes: string[]
): string => {
  let cypher = '// Neo4j Schema DDL\n\n';

  cypher += '// Node Constraints\n';
  constraints.forEach(c => {
    cypher += `${c};\n`;
  });

  cypher += '\n// Node Indexes\n';
  indexes.forEach(i => {
    cypher += `${i};\n`;
  });

  cypher += '\n// Sample Node Creation\n';
  nodes.slice(0, 2).forEach(node => {
    cypher += `// Create ${node.label}\n`;
    cypher += `CREATE (n:${node.label} {\n`;
    node.properties.slice(0, 3).forEach((prop, idx) => {
      const comma = idx < 2 ? ',' : '';
      cypher += `  ${prop.name}: <${prop.type}>${comma}\n`;
    });
    cypher += '});\n\n';
  });

  return cypher;
};

export const Step4SchemaPreviewEnhanced: React.FC<Step4Props> = ({ onNext, onBack }) => {
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  const { dataMappings, schemaDesign, setSchemaDesign, setSchemaPreviews, questionCanvases, selectedSystemIds } = useAnalysisStore();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  const [hasSavedSchema, setHasSavedSchema] = useState(false);
  const [savedSchemaVersion, setSavedSchemaVersion] = useState<number | null>(null);
  // Advanced Design state - existing selected tables from Neo4j
  const [existingAdvancedTables, setExistingAdvancedTables] = useState<SelectedTableInfo[]>([]);
  const [isLoadingAdvancedTables, setIsLoadingAdvancedTables] = useState(false);
  const [hasExistingAdvancedSelection, setHasExistingAdvancedSelection] = useState(false);
  const [lastGeneratedSchema, setLastGeneratedSchema] = useState<QuickSchemaGenerationResponse | null>(null);
  const [showCypher, setShowCypher] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('visual'); // Default to visual view
  const [designMode, setDesignMode] = useState<DesignMode>(null);
  const [selectedSystemIdsLocal, setSelectedSystemIdsLocal] = useState<string[]>([]);
  const [wizardStarted, setWizardStarted] = useState(false);
  // Quick Generation's Suggested Ontology — OPT-IN, not a pre-step. Quick opens
  // exactly as its card advertises ("auto-generate from your enterprise system
  // tables · Fast - generates in seconds"); the user opts in via "Start from
  // suggested ontology". Advanced Design keeps the suggestion as its up-front step
  // (unchanged) — that's the Quick/Advanced distinction.
  const [showSuggestedQuick, setShowSuggestedQuick] = useState(false);
  // Origin of the schema currently in the store. Set when the user accepts a
  // suggested ontology (and restored from the server-side accepted suggestion on
  // re-mount); cleared whenever the table-driven generator runs. Regenerate reads
  // this so it re-runs the SAME source the schema came from — it previously always
  // called the table-driven generate-stream, silently discarding an accepted ontology
  // and rebuilding from enterprise-system tables.
  const [schemaFromSuggestion, setSchemaFromSuggestion] = useState(false);
  const [isStreamingGeneration, setIsStreamingGeneration] = useState(false);
  const [streamingParams, setStreamingParams] = useState<{
    systemId: string;
    selectedTables: string[];
    tables: QuickSchemaTable[];
    strategicQuestions: string[];
    businessContext?: string;
  } | null>(null);

  // Convert SchemaDesign to Neo4jSchema format for visualizer
  const convertToNeo4jSchema = (design: SchemaDesign): Neo4jSchema => {
    return {
      version: '1.0',
      nodes: design.nodes.map(node => ({
        label: node.label,
        properties: node.properties.map(p => ({
          name: p.name,
          type: p.type,
          required: p.required,
          description: `${p.name} property`
        })),
        description: `${node.label} node type`
      })),
      relationships: design.relationships.map(rel => ({
        type: rel.type,
        sourceLabel: rel.from,
        targetLabel: rel.to,
        properties: rel.properties?.map(p => ({
          name: p.name,
          type: p.type,
          required: false,
          description: `${p.name} property`
        })) || [],
        description: `${rel.from} to ${rel.to} relationship`
      })),
      constraints: design.constraints.map((c, idx) => ({
        name: `constraint_${idx}`,
        type: 'UNIQUE' as const,
        label: c.split('FOR (n:')[1]?.split(')')[0] || 'Unknown',
        properties: [c.split('n.')[1]?.split(' ')[0] || 'unknown']
      })),
      indexes: design.indexes.map((i, idx) => ({
        name: `index_${idx}`,
        label: i.split('FOR (n:')[1]?.split(')')[0] || 'Unknown',
        properties: [i.split('n.')[1]?.split(')')[0] || 'unknown']
      })),
      ddl: design.cypherDDL || ''
    };
  };

  const approvedMappings = dataMappings.filter(m => m.approved);

  // Extract unique data sources from question canvases, merging tables from all canvases
  const systemDataSources = useMemo(() => {
    const sourcesMap = new Map<string, SystemDataSource>();
    questionCanvases?.forEach(canvas => {
      canvas.data_sources?.forEach(source => {
        if (!sourcesMap.has(source.system_id)) {
          // First occurrence - clone the source
          sourcesMap.set(source.system_id, {
            ...source,
            tables: [...(source.tables || [])],
          });
        } else {
          // Merge tables from this canvas into existing entry
          const existing = sourcesMap.get(source.system_id)!;
          const existingTableNames = new Set(existing.tables.map(t => t.table_name));
          source.tables?.forEach(table => {
            if (!existingTableNames.has(table.table_name)) {
              existing.tables.push(table);
              existingTableNames.add(table.table_name);
            }
          });
        }
      });
    });
    return Array.from(sourcesMap.values());
  }, [questionCanvases]);

  // Check if we have enterprise systems with tables
  const hasEnterpriseSystems = useMemo(() => {
    const hasSystems = systemDataSources.length > 0 &&
           systemDataSources.some((sys: SystemDataSource) => sys.tables && sys.tables.length > 0);

    // Debug logging
    console.log('Step4 Schema Preview - systemDataSources:', systemDataSources.length, 'systems');
    systemDataSources.forEach(sys => {
      console.log(`  - ${sys.system_name}: ${sys.tables?.length || 0} tables`);
    });
    console.log('hasEnterpriseSystems:', hasSystems);

    return hasSystems;
  }, [systemDataSources]);

  // Resolve the current user's email for the X-User-Email header. Prefer the
  // localStorage 'currentUser' key (now written by the auth store on login), then
  // fall back to the auth store itself (single source of truth) before any
  // placeholder — a bogus 'unknown@example.com' fails the backend case-access
  // check (403) on case-scoped calls like the Suggested-Ontology generate.
  const currentUser = useMemo(() => {
    try {
      const user = localStorage.getItem('currentUser');
      if (user) return JSON.parse(user);
    } catch {
      /* ignore malformed/blocked storage */
    }
    const email = useAppStore.getState().currentUser?.email;
    return { email: email || 'unknown@example.com' };
  }, []);

  // Fetch saved schema on mount or when entering quick mode.
  // Ref-guarded per caseId so it fires once — plain [caseId] effects re-run
  // under StrictMode's dev double-invoke (and on re-mount), which is what
  // produced the duplicate `schema` calls in the Network tab.
  const savedSchemaFetchedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!caseId) return;
    if (savedSchemaFetchedForRef.current === caseId) return;
    savedSchemaFetchedForRef.current = caseId;

    const fetchSavedSchema = async () => {
      if (!caseId) return;

      setIsLoadingSaved(true);
      try {
        console.log('🔍 Checking for saved schema for case:', caseId);
        const savedSchema = await getSavedSchema(caseId);

        if (savedSchema && savedSchema.status === 'completed' && savedSchema.schema) {
          console.log('✅ Found saved schema, version:', savedSchema.version);
          console.log('   Nodes:', savedSchema.schema.nodes?.length || 0);
          console.log('   Relationships:', savedSchema.schema.relationships?.length || 0);

          // Convert saved schema to SchemaDesign format
          const schema = savedSchema.schema;
          const converted: SchemaDesign = {
            nodes: (schema.nodes || []).map(node => ({
              label: node.label,
              properties: (node.properties || []).map(p => ({
                name: p.name,
                type: p.type || 'String',
                required: p.required || false,
                unique: p.unique || false,
                indexed: p.indexed || false,
              })),
            })),
            relationships: (schema.relationships || []).map(rel => ({
              type: rel.type,
              from: rel.from,
              to: rel.to,
              properties: (rel.properties || []).map(p => ({
                name: p.name,
                type: p.type || 'String',
              })),
            })),
            constraints: (schema.constraints || []).map((c: any) =>
              typeof c === 'string' ? c :
              `CREATE CONSTRAINT ${c.name} IF NOT EXISTS FOR (n:${c.label}) REQUIRE (${c.properties.map((p: string) => `n.${p}`).join(', ')}) IS ${c.type === 'unique' ? 'UNIQUE' : 'NODE KEY'}`
            ),
            indexes: (schema.indexes || []).map((i: any) =>
              typeof i === 'string' ? i :
              `CREATE INDEX ${i.name} IF NOT EXISTS FOR (n:${i.label}) ON (${i.properties.map((p: string) => `n.${p}`).join(', ')})`
            ),
            cypherDDL: schema.cypherDDL || '',
          };

          // Only claim a saved schema when it actually carries nodes.
          // `setHasSavedSchema(true)` used to fire on any completed record, so
          // a saved-but-empty schema rendered the "Saved Schema Loaded" banner
          // while the body fell through to the `!schemaDesign` branch and said
          // "No schema generated yet" — the two contradictory panels.
          if (converted.nodes.length > 0) {
            setSchemaDesign(converted);
            setHasSavedSchema(true);
            setSavedSchemaVersion(savedSchema.version);
          } else {
            console.warn(
              '⚠️ Saved schema for case',
              caseId,
              'contains 0 nodes — treating as "no schema" rather than showing an empty saved-schema banner.',
            );
            setHasSavedSchema(false);
            setSavedSchemaVersion(null);
          }

          // Auto-select quick mode if we have a saved schema
          if (designMode === null && hasEnterpriseSystems) {
            setDesignMode('quick');
          }
        } else {
          console.log('📭 No saved schema found for case:', caseId);
          setHasSavedSchema(false);
          setSavedSchemaVersion(null);
        }
      } catch (error) {
        console.warn('⚠️ Error fetching saved schema:', error);
        setHasSavedSchema(false);
      } finally {
        setIsLoadingSaved(false);
      }
    };

    fetchSavedSchema();
  }, [caseId, setSchemaDesign]);

  // Restore the schema's ORIGIN across remounts. `schemaFromSuggestion` is component
  // state, so navigating Step 4 -> Review -> back re-mounts this step and would reset
  // it — after which Regenerate fell back to the table-driven generate-stream and
  // silently rebuilt the schema from enterprise-system tables, discarding the accepted
  // ontology. Acceptance is persisted server-side by SuggestedOntologyView
  // (status -> accepted), so an accepted suggestion for this case means the schema
  // came from the ontology. Ref-guarded per caseId (see savedSchemaFetchedForRef) so
  // StrictMode's double-invoke doesn't duplicate the call. Best-effort: on any error
  // we simply leave the legacy table-driven Regenerate in place.
  const schemaOriginCheckedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!caseId || !SUGGESTED_SCHEMA_FLAG) return;
    if (schemaOriginCheckedForRef.current === caseId) return;
    schemaOriginCheckedForRef.current = caseId;
    ontologySuggestionApi
      .getSuggestionForCase(caseId, currentUser.email)
      .then((saved) => {
        if (saved?.status === 'accepted') setSchemaFromSuggestion(true);
      })
      .catch(() => {
        /* non-fatal — Regenerate stays on the table-driven path */
      });
  }, [caseId, currentUser.email]);

  // Check for existing Advanced Design tables (from AI Digital Twin selection).
  // Ref-guarded per caseId (see savedSchemaFetchedForRef) so it fires once and
  // doesn't duplicate the `selected` (getSelectedTablesForCase) call.
  const advancedTablesFetchedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!caseId) return;
    if (advancedTablesFetchedForRef.current === caseId) return;
    advancedTablesFetchedForRef.current = caseId;

    const fetchExistingAdvancedTables = async () => {
      if (!caseId) return;

      setIsLoadingAdvancedTables(true);
      try {
        console.log('🔍 Checking for existing Advanced Design tables for case:', caseId);
        const tables = await getSelectedTablesForCase(caseId);

        if (tables && tables.length > 0) {
          console.log('✅ Found existing Advanced Design tables:', tables.length);
          setExistingAdvancedTables(tables);
          setHasExistingAdvancedSelection(true);
        } else {
          console.log('📭 No existing Advanced Design tables found');
          setHasExistingAdvancedSelection(false);
        }
      } catch (error) {
        console.warn('⚠️ Error fetching existing Advanced Design tables:', error);
        setHasExistingAdvancedSelection(false);
      } finally {
        setIsLoadingAdvancedTables(false);
      }
    };

    fetchExistingAdvancedTables();
  }, [caseId]);

  // Auto-select systems when there's a saved schema or in advanced mode (for returning users)
  useEffect(() => {
    // Auto-select systems with tables when:
    // 1. We have a saved schema, OR
    // 2. User is in advanced mode and systems haven't been selected yet
    const shouldAutoSelect =
      (hasSavedSchema || designMode === 'advanced') &&
      systemDataSources.length > 0 &&
      selectedSystemIdsLocal.length === 0;

    if (shouldAutoSelect) {
      // Pre-select all systems that have tables
      const systemsWithTables = systemDataSources
        .filter((sys: SystemDataSource) => sys.tables && sys.tables.length > 0)
        .map((sys: SystemDataSource) => sys.system_id);

      if (systemsWithTables.length > 0) {
        console.log('✅ Auto-selecting systems for saved schema:', systemsWithTables);
        setSelectedSystemIdsLocal(systemsWithTables);
      }
    }
  }, [hasSavedSchema, designMode, systemDataSources, selectedSystemIdsLocal.length]);

  // Handler for streaming completion
  const handleStreamingComplete = useCallback(async (result: SchemaGenerationResult) => {
    console.log('Streaming generation complete:', result);

    // Convert streaming result to SchemaDesign format
    const generated: SchemaDesign = {
      nodes: result.nodes.map(node => ({
        label: node.label,
        properties: (node.properties || []).map(p => ({
          name: p.name,
          type: p.type || 'String',
          required: p.required || false,
          unique: p.unique || false,
          indexed: p.indexed || false,
        })),
      })),
      relationships: result.relationships.map(rel => ({
        type: rel.type,
        from: rel.from,
        to: rel.to,
        properties: (rel.properties || []).map(p => ({
          name: p.name,
          type: p.type || 'String',
        })),
      })),
      constraints: (result.constraints || []).map(c =>
        `CREATE CONSTRAINT ${c.name} IF NOT EXISTS FOR (n:${c.label}) REQUIRE (${c.properties.map(p => `n.${p}`).join(', ')}) IS ${c.type === 'unique' || c.type === 'UNIQUE' ? 'UNIQUE' : 'NODE KEY'}`
      ),
      indexes: (result.indexes || []).map(i =>
        `CREATE INDEX ${i.name} IF NOT EXISTS FOR (n:${i.label}) ON (${i.properties.map(p => `n.${p}`).join(', ')})`
      ),
      cypherDDL: result.cypher_ddl || '',
    };

    setSchemaDesign(generated);
    setIsStreamingGeneration(false);
    setStreamingParams(null);

    // PERSIST the generated schema now, and claim "saved" only from the
    // server's answer. This used to set hasSavedSchema(true) with no request,
    // so the step announced a saved schema that existed only in this browser
    // (no SchemaDesign node until Review's Complete mutation ran). The store
    // update above remains the client-side cache.
    setHasSavedSchema(false);
    setSavedSchemaVersion(null);
    if (!caseId) return;
    try {
      const saved = await saveQuickSchema(caseId, {
        schema_id: result.schema_id,
        nodes: result.nodes,
        relationships: result.relationships,
        constraints: result.constraints || [],
        indexes: result.indexes || [],
        cypherDDL: result.cypher_ddl || '',
      } as QuickSchemaGenerationResponse);
      if (saved && saved.success === false) {
        throw new Error(saved.message || 'Server rejected the schema save');
      }
      setHasSavedSchema(true);
      setSavedSchemaVersion(saved?.version ?? null);
    } catch (err) {
      console.error('saveQuickSchema (stream completion) failed', err);
      setHasSavedSchema(false);
      setSavedSchemaVersion(null);
      toast.error('Schema generated but not saved — retry or complete the phase to save');
    }
  }, [caseId, setSchemaDesign]);

  // Handler for streaming error
  const handleStreamingError = useCallback((error: string) => {
    console.error('Streaming generation error:', error);
    toast.error('Schema generation failed', { description: error });
    setIsStreamingGeneration(false);
    setStreamingParams(null);
  }, []);

  // Handler for streaming cancel
  const handleStreamingCancel = useCallback(() => {
    setIsStreamingGeneration(false);
    setStreamingParams(null);
  }, []);

  const handleGenerateSchema = async () => {
    console.log('handleGenerateSchema called');
    // Table-driven generation is now the schema's origin (clears any prior
    // accepted-ontology origin so Regenerate keeps re-running the tables).
    setSchemaFromSuggestion(false);
    console.log('  caseId:', caseId);
    console.log('  hasEnterpriseSystems:', hasEnterpriseSystems);
    console.log('  systemDataSources:', systemDataSources.length);
    console.log('  approvedMappings:', approvedMappings.length);

    setIsGenerating(true);

    // AI-based schema generation (directly from tables)
    if (caseId && hasEnterpriseSystems) {
      try {
        console.log('🤖 Starting AI-based schema generation from tables...');

        // Build request with all tables from all systems
        const allTables: QuickSchemaTable[] = [];

        systemDataSources.forEach((sys: SystemDataSource) => {
          if (sys.tables && sys.tables.length > 0) {
            sys.tables.forEach(table => {
              // Convert columns to the format expected by the API
              // table.columns is of type CategorizedColumns (must, should, could arrays)
              const allCols = [
                ...(table.columns?.must || []),
                ...(table.columns?.should || []),
                ...(table.columns?.could || []),
              ];

              const columns: QuickSchemaTableColumn[] = allCols.map(col => ({
                column_name: col.column_name || '',
                data_type: col.data_type || 'String',
                is_primary_key: col.is_primary_key || false,
                is_foreign_key: col.is_foreign_key || false,
                foreign_key_reference: undefined,
                description: col.description,
                business_meaning: undefined,
              }));

              allTables.push({
                table_id: table.table_id || `${sys.system_id}_${table.table_name}`,
                table_name: table.table_name || '',
                module_name: table.module,
                system_name: sys.system_name,
                description: table.table_description,
                columns,
                primary_keys: [], // TableMapping doesn't have primary_keys
              });
            });
          }
        });

        console.log(`📋 Sending ${allTables.length} tables to AI for schema generation`);

        // Get strategic questions if available (from question_text in each canvas)
        const strategicQuestions = questionCanvases
          .map(canvas => canvas.question_text || '')
          .filter(q => q.length > 0);

        // Check if any tables have columns - if not, backend will auto-fetch from Neo4j
        const tablesHaveColumns = allTables.some(t => t.columns && t.columns.length > 0);

        // Get primary system_id for auto-fetch
        // Priority: (1) system with tables, (2) first available system, (3) selectedSystemIds
        const primarySystem = systemDataSources.find(s => s.tables && s.tables.length > 0)
                           || systemDataSources[0];
        const systemId = primarySystem?.system_id || selectedSystemIds?.[0];
        const selectedTableNames = allTables.map(t => t.table_name);

        console.log(`📊 Tables have columns: ${tablesHaveColumns}, System ID: ${systemId}`);
        console.log(`📋 Selected tables: ${selectedTableNames.length}`);
        console.log(`📊 systemDataSources count: ${systemDataSources.length}, selectedSystemIds: ${selectedSystemIds?.length || 0}`);
        console.log(`🔍 System ID format check: ${systemId?.startsWith('es_') ? '✅ Neo4j format' : '⚠️ Frontend format - may need regeneration in Step 3'}`);

        // We need either tables OR a system_id (backend can auto-fetch tables from Neo4j)
        if (allTables.length === 0 && !systemId) {
          console.error('❌ No tables or system_id available for schema generation');
          toast.error('No data sources selected', {
            description: 'Go back and select an enterprise system or its tables before generating.',
          });
          setIsGenerating(false);
          return;
        }

        // Use SSE streaming for real-time progress updates
        console.log('🎬 Starting streaming schema generation...');
        console.log(`   System ID: ${systemId}`);
        console.log(`   Tables: ${selectedTableNames.length}`);
        console.log(`   Questions: ${strategicQuestions.length}`);

        // Set streaming parameters - the component will handle the rest.
        // Send the FULL table objects (with columns) the user mapped in Step 3 so the
        // backend generates directly from them — independent of Neo4j enrichment.
        setStreamingParams({
          systemId: systemId!,
          selectedTables: selectedTableNames,
          tables: allTables,
          strategicQuestions: strategicQuestions,
          businessContext: undefined,
        });
        setIsStreamingGeneration(true);
        setIsGenerating(false); // Turn off static loading
        return;
      } catch (error: any) {
        console.error('❌ AI schema generation failed:', error);
        const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
        toast.error('Schema generation failed', {
          description: `${errorMessage}. Check that the backend and LLM service are running (see console for details).`,
        });
        setIsGenerating(false);
        return;
      }
    }

    // No enterprise systems - check for approved mappings
    if (approvedMappings.length > 0) {
      console.log('📋 Using generateSchemaFromMappings (from approved data mappings)');
      const generated = generateSchemaFromMappings(approvedMappings);
      console.log('Generated schema:', generated.nodes.length, 'nodes,', generated.relationships.length, 'relationships');
      setSchemaDesign(generated);
      setIsGenerating(false);
      return;
    }

    // No data sources available
    console.log('❌ No data sources available - cannot generate schema');
    toast.error('No data sources available', {
      description: 'No enterprise systems or approved data mappings yet. Go back to select data sources.',
    });
    setIsGenerating(false);
  };

  const handleKGDComplete = async () => {
    // Fetch the generated schema from the API
    if (caseId && selectedSystemIdsLocal.length > 0) {
      try {
        setIsGenerating(true);
        console.log('Fetching schema preview for Advanced Design completion...');

        // Fetch schema for each selected system and merge
        const allSchemaData: SchemaDesign = {
          nodes: [],
          relationships: [],
          constraints: [],
          indexes: [],
          cypherDDL: '',
        };

        // Retain each raw server preview alongside the UI projection.
        // convertSchemaPreviewToDesign() is lossy — it drops mermaid_diagram,
        // coverage_analysis and gds_recommendations, all of which
        // POST /schema/complete needs. Step 5 forwards these verbatim.
        const rawPreviews: SchemaPreview[] = [];

        for (const systemId of selectedSystemIdsLocal) {
          try {
            const preview = await generateSchemaPreview(caseId, systemId, true, true);
            console.log(`Schema preview for ${systemId}:`, preview.tables?.length, 'tables,', preview.relationships?.length, 'relationships');

            if (preview.tables && preview.tables.length > 0) {
              rawPreviews.push(preview);
              const converted = convertSchemaPreviewToDesign(preview);
              allSchemaData.nodes.push(...converted.nodes);
              allSchemaData.relationships.push(...converted.relationships);
              allSchemaData.constraints.push(...converted.constraints);
              allSchemaData.indexes.push(...converted.indexes);
            }
          } catch (err) {
            console.warn(`Failed to fetch schema for system ${systemId}:`, err);
          }
        }

        setSchemaPreviews(rawPreviews);

        // Regenerate combined Cypher DDL
        if (allSchemaData.nodes.length > 0) {
          allSchemaData.cypherDDL = generateCypherDDL(
            allSchemaData.nodes,
            allSchemaData.relationships,
            allSchemaData.constraints,
            allSchemaData.indexes
          );
        }

        console.log('Combined schema:', allSchemaData.nodes.length, 'nodes,', allSchemaData.relationships.length, 'relationships');
        setSchemaDesign(allSchemaData);
      } catch (error) {
        console.error('Failed to fetch schema preview:', error);
      } finally {
        setIsGenerating(false);
      }
    }

    setDesignMode(null);
  };

  // Mode selection screen
  if (designMode === null && hasEnterpriseSystems) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Schema Design Approach
          </h2>
          <p className="text-neutral-600">
            Choose how you'd like to design your knowledge graph schema
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Quick Mode */}
          <Card
            hover
            onClick={() => setDesignMode('quick')}
            className={`cursor-pointer border-2 transition-all ${
              hasSavedSchema ? 'border-green-300 hover:border-green-400 bg-green-50/30' : 'hover:border-primary-400'
            }`}
          >
            <CardBody className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  hasSavedSchema ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  {hasSavedSchema ? (
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  ) : (
                    <Sparkles className="w-6 h-6 text-blue-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Quick Generation</h3>
                  {hasSavedSchema ? (
                    <Badge variant="success">Schema already generated</Badge>
                  ) : (
                    <Badge variant="info">Recommended for simple schemas</Badge>
                  )}
                </div>
              </div>
              <p className="text-neutral-600 mb-4">
                {hasSavedSchema ? (
                  <>View your previously generated schema or regenerate with updated data.</>
                ) : SUGGESTED_SCHEMA_FLAG ? (
                  // Gated on the same flag as the "Start from suggested ontology"
                  // button below, so the card never promises an option that isn't shown.
                  <>Start from an AI-suggested ontology for your company, or auto-generate
                  from your enterprise system tables. Best for straightforward use cases.</>
                ) : (
                  <>Automatically generate schema from your enterprise system tables.
                  Best for straightforward use cases.</>
                )}
              </p>
              <ul className="space-y-2 text-sm text-neutral-600">
                {hasSavedSchema ? (
                  <>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Schema saved - version {savedSchemaVersion || 1}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      {schemaDesign?.nodes?.length || 0} nodes, {schemaDesign?.relationships?.length || 0} relationships
                    </li>
                    <li className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-blue-500" />
                      View, edit, or regenerate anytime
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Fast - generates in seconds
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      {SUGGESTED_SCHEMA_FLAG ? 'Suggested ontology, or ' : 'Uses '}
                      {systemDataSources.reduce((total, sys) => total + (sys.tables?.length || 0), 0)} tables from {systemDataSources.length} system{systemDataSources.length !== 1 ? 's' : ''}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Auto-generates constraints & indexes
                    </li>
                  </>
                )}
              </ul>
              <Button variant={hasSavedSchema ? 'primary' : 'outline'} className="mt-4 w-full">
                {hasSavedSchema ? (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    View Quick Schema
                  </>
                ) : (
                  <>
                    Use Quick Generation
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </CardBody>
          </Card>

          {/* Advanced Mode */}
          <Card
            hover
            onClick={() => setDesignMode('advanced')}
            className={`cursor-pointer border-2 transition-all ${
              hasExistingAdvancedSelection ? 'border-purple-300 hover:border-purple-400 bg-purple-50/30' : 'hover:border-secondary-400'
            }`}
          >
            <CardBody className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  hasExistingAdvancedSelection ? 'bg-purple-100' : 'bg-purple-100'
                }`}>
                  {hasExistingAdvancedSelection ? (
                    <CheckCircle2 className="w-6 h-6 text-purple-600" />
                  ) : (
                    <Settings2 className="w-6 h-6 text-purple-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Advanced Design</h3>
                  {hasExistingAdvancedSelection ? (
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                      {existingAdvancedTables.length} tables selected
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Full control over schema</Badge>
                  )}
                </div>
              </div>
              <p className="text-neutral-600 mb-4">
                {hasExistingAdvancedSelection ? (
                  <>View your previously selected tables from AI Digital Twin, or modify your selection.</>
                ) : (
                  <>Use the Knowledge Graph Design wizard to explore tables,
                  discover relationships, and preview your schema interactively.</>
                )}
              </p>
              <ul className="space-y-2 text-sm text-neutral-600">
                {hasExistingAdvancedSelection ? (
                  <>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-500" />
                      {existingAdvancedTables.length} tables selected via AI Digital Twin
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-500" />
                      {[...new Set(existingAdvancedTables.map(t => t.system_name))].length} enterprise system(s)
                    </li>
                    <li className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-blue-500" />
                      View selection or modify anytime
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-blue-500" />
                      Browse & select from {systemDataSources?.length || 0} enterprise systems
                    </li>
                    <li className="flex items-center gap-2">
                      <GitBranch className="w-4 h-4 text-purple-500" />
                      AI-powered relationship discovery
                    </li>
                    <li className="flex items-center gap-2">
                      <LayoutDashboard className="w-4 h-4 text-green-500" />
                      Visual ER diagram & GDS recommendations
                    </li>
                  </>
                )}
              </ul>
              <Button variant={hasExistingAdvancedSelection ? 'primary' : 'outline'} className="mt-4 w-full">
                {hasExistingAdvancedSelection ? (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    View Advanced Selection
                  </>
                ) : (
                  <>
                    Use Advanced Design
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </CardBody>
          </Card>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  // Quick Generation — Suggested Ontology pre-step. Parity with Advanced Design: the
  // SAME company-aware suggestion (auto-detected industry/domain, Comprehensive
  // multi-domain, provenance, graph) is offered here too. Accepting hands the
  // suggested schema straight into Quick's existing schema state, so the normal
  // preview/save/visualize path renders it with no table-by-table generation.
  // Skipping drops into the legacy auto-generate-from-tables flow, so the existing
  // Quick behavior is unchanged (and it's flag-gated + skipped when a schema exists).
  if (designMode === 'quick' && SUGGESTED_SCHEMA_FLAG && showSuggestedQuick && caseId) {
    return (
      <div className="flex flex-col bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Suggested Ontology / Schema</h2>
            <p className="text-sm text-neutral-500">
              Pick an industry/domain and let the agent suggest a starting ontology.
            </p>
          </div>
          <Button variant="ghost" onClick={() => setShowSuggestedQuick(false)}>
            Skip to auto-generate
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <SuggestedOntologyView
            caseId={caseId}
            currentUser={currentUser.email}
            onBack={onBack}
            onAccept={async (_schema, suggestion) => {
              setSchemaDesign(suggestionToStoreSchema(suggestion));
              // Remember the origin so Regenerate re-runs the ontology rather than the
              // table-driven generate-stream (which would discard this schema).
              setSchemaFromSuggestion(true);
              setShowSuggestedQuick(false);
              // PERSIST the accepted ontology as the case's schema. Without this it
              // lives only in the in-memory store, so returning to Step 4 re-runs
              // getSavedSchema and OVERWRITES it with the older table-generated
              // schema ("Saved Schema Loaded" showing the previous nodes). Saving
              // makes the ontology the case's schema for Review + the Design phase.
              try {
                const saved = await saveQuickSchema(caseId, {
                  schema_id: suggestion.suggestionId,
                  nodes: suggestion.schemaDesign.nodes,
                  relationships: suggestion.schemaDesign.relationships,
                  constraints: suggestion.schemaDesign.constraints,
                  indexes: suggestion.schemaDesign.indexes,
                  cypherDDL: suggestion.cypherDdlPreview,
                } as unknown as QuickSchemaGenerationResponse);
                setHasSavedSchema(true);
                setSavedSchemaVersion(saved?.version ?? null);
                toast.success('Suggested ontology saved as this case’s schema');
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error('saveQuickSchema (suggested ontology) failed', err);
                toast.error(
                  'Schema applied, but saving it failed — it may be replaced by the previously saved schema'
                );
              }
            }}
            onCancel={() => setDesignMode(null)}
          />
        </div>
      </div>
    );
  }

  // Advanced mode - Knowledge Graph Design Wizard
  if (designMode === 'advanced') {
    // Toggle system selection
    const toggleSystemSelection = (systemId: string) => {
      setSelectedSystemIdsLocal(prev =>
        prev.includes(systemId)
          ? prev.filter(id => id !== systemId)
          : [...prev, systemId]
      );
    };

    // Select/Deselect all
    const toggleSelectAll = () => {
      if (selectedSystemIdsLocal.length === systemDataSources.length) {
        setSelectedSystemIdsLocal([]);
      } else {
        setSelectedSystemIdsLocal(systemDataSources.map((s: SystemDataSource) => s.system_id));
      }
    };

    // System selection if wizard not started
    if (!wizardStarted && systemDataSources && systemDataSources.length > 0) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-neutral-900">Select Enterprise Systems</h2>
              <p className="text-neutral-600">Choose one or more systems to design the knowledge graph</p>
            </div>
            <Button variant="outline" onClick={() => setDesignMode(null)}>
              Back to Mode Selection
            </Button>
          </div>

          {/* Select All / Deselect All */}
          <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
            <span className="text-sm text-neutral-600">
              {selectedSystemIdsLocal.length} of {systemDataSources.length} systems selected
            </span>
            <Button variant="ghost" size="sm" onClick={toggleSelectAll}>
              {selectedSystemIdsLocal.length === systemDataSources.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          <div className="grid gap-4">
            {systemDataSources.map((system: SystemDataSource) => {
              const isSelected = selectedSystemIdsLocal.includes(system.system_id);
              return (
                <Card
                  key={system.system_id}
                  hover
                  onClick={() => toggleSystemSelection(system.system_id)}
                  className={`cursor-pointer transition-all ${isSelected ? 'border-2 border-primary-500 bg-primary-50' : ''}`}
                >
                  <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Checkbox */}
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                          isSelected ? 'bg-primary-500 border-primary-500' : 'border-neutral-300'
                        }`}>
                          {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                          <Database className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold">{system.system_name}</h4>
                          <p className="text-sm text-neutral-500">{system.vendor}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{system.tables?.length || 0} tables</Badge>
                        <Badge
                          variant={system.role === 'primary' ? 'primary' : 'secondary'}
                        >
                          {system.role}
                        </Badge>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>

          {/* Continue Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={() => setWizardStarted(true)}
              disabled={selectedSystemIdsLocal.length === 0}
            >
              Continue with {selectedSystemIdsLocal.length} System{selectedSystemIdsLocal.length !== 1 ? 's' : ''}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      );
    }

    // Knowledge Graph Design Wizard - pass all selected systems
    const selectedSystems = systemDataSources?.filter((s: SystemDataSource) =>
      selectedSystemIdsLocal.includes(s.system_id)
    );

    if (selectedSystems && selectedSystems.length > 0 && caseId) {
      // min-h instead of fixed h: the previous calc(100vh-200px) forced ~880px
      // regardless of content, leaving a tall mostly-empty card whenever the
      // schema was modest and pushing the outer Save Draft button down into
      // an awkward void.  min-h gives the wizard a sensible floor while
      // letting it grow naturally for large schemas (the internal `flex-1
      // overflow-y-auto` still handles scroll for truly huge ones).
      return (
        <div className="min-h-[600px]">
          <KnowledgeGraphDesignWizard
            caseId={caseId}
            systemIds={selectedSystemIdsLocal}
            systemNames={selectedSystems.map((s: SystemDataSource) => s.system_name)}
            currentUser={currentUser.email}
            onComplete={(result, savedPreview) => {
              if (result?.success) {
                // Continue to "Review & Complete" — the next step of THIS
                // wizard. This previously did navigate('/'), which dropped the
                // user on the dashboard and skipped the final step entirely.
                //
                // Seed the analysis store from the preview we just saved:
                // Step 5's checklist reads `schemaDesign` from the store, and
                // the Advanced path never populated it, so the user would land
                // on "Validation Incomplete" with 0 Schema Nodes.
                if (savedPreview) {
                  setSchemaDesign(convertSchemaPreviewToDesign(savedPreview));
                }
                onNext();
              } else {
                // Fallback to original behavior
                handleKGDComplete();
              }
            }}
            onCancel={() => {
              setSelectedSystemIdsLocal([]);
              setWizardStarted(false);
              setDesignMode(null);
            }}
            onBack={onBack}
          />
        </div>
      );
    }
  }

  // Quick mode - Loading saved schema
  if (isLoadingSaved) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Loading Schema
          </h2>
          <p className="text-neutral-600">
            Checking for previously saved schema...
          </p>
        </div>

        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardBody>
            <div className="flex items-center gap-3">
              <div className="animate-spin">
                <Database className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-neutral-900 mb-1">
                  Loading saved schema from Neo4j...
                </p>
                <p className="text-sm text-neutral-600">
                  Fetching your previously generated schema
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Quick mode - SSE Streaming schema generation
  if (isStreamingGeneration && streamingParams && caseId) {
    return (
      <SchemaGenerationProgress
        caseId={caseId}
        systemId={streamingParams.systemId}
        selectedTables={streamingParams.selectedTables}
        tables={streamingParams.tables}
        strategicQuestions={streamingParams.strategicQuestions}
        businessContext={streamingParams.businessContext}
        onComplete={handleStreamingComplete}
        onError={handleStreamingError}
        onCancel={handleStreamingCancel}
      />
    );
  }

  // Quick mode - Fallback static loading (for non-streaming operations)
  if (isGenerating) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Generating Neo4j Schema
          </h2>
          <p className="text-neutral-600">
            AI is designing your optimal graph database schema
          </p>
        </div>

        <Card className="bg-gradient-to-r from-purple-50 to-blue-50">
          <CardBody>
            <div className="flex items-center gap-3">
              <div className="animate-spin">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-neutral-900 mb-1">
                  Schema Generation in Progress...
                </p>
                <p className="text-sm text-neutral-600">
                  Analyzing data mappings, creating nodes and relationships, optimizing indexes
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!schemaDesign) {
    // Detect the "selected systems have no table research data" state — most
    // common after picking a library-seeded system (e.g., SAP Customer
    // Experience / SAP CRM straight from the catalog).  We surface a clear
    // explanation + actionable next steps instead of a dead-end message.
    const hasSystemsWithoutTables =
      systemDataSources.length > 0 &&
      !systemDataSources.some((s: SystemDataSource) => s.tables && s.tables.length > 0);

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Neo4j Schema Design
          </h2>
          <p className="text-neutral-600">
            Generate your graph database schema from approved mappings
          </p>
        </div>

        {/* Back-to-Mode-Selection — now always visible, not gated on data
            availability.  Previously this was hidden when there were no tables,
            stranding the user on an empty Schema Design screen with no way
            back to the Quick/Advanced choice. */}
        <Button
          variant="outline"
          className="mb-4"
          onClick={() => setDesignMode(null)}
        >
          Back to Mode Selection
        </Button>

        <Card>
          <CardBody>
            <div className="text-center py-12">
              <Database className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                {hasSystemsWithoutTables
                  ? 'Selected systems have no table data yet'
                  : 'Ready to Generate Schema'}
              </h3>
              <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
                {hasEnterpriseSystems ? (
                  <>
                    {systemDataSources.reduce((total, sys) => total + (sys.tables?.length || 0), 0)} tables from {systemDataSources.length} enterprise system{systemDataSources.length !== 1 ? 's' : ''} will be used to create your Neo4j schema
                  </>
                ) : approvedMappings.length > 0 ? (
                  <>
                    {approvedMappings.length} approved mappings will be used to create your Neo4j schema
                  </>
                ) : hasSystemsWithoutTables ? (
                  <>
                    The {systemDataSources.length} system{systemDataSources.length !== 1 ? 's' : ''} you selected
                    {systemDataSources.length === 1
                      ? ` (${systemDataSources[0].system_name})`
                      : ''}{' '}
                    {systemDataSources.length === 1 ? 'is' : 'are'} catalog-only — modules, tables,
                    and columns haven't been researched yet, so there's nothing to derive a graph
                    schema from.  Go back to Step 2 and pick a system with research data (e.g., one
                    auto-generated for your industry, or one previously researched), or add a custom
                    system that maps to data you have.
                  </>
                ) : (
                  <>
                    No data sources available. Please go back to select enterprise systems or approve
                    data mappings.
                  </>
                )}
              </p>
              <Button onClick={handleGenerateSchema} disabled={!hasEnterpriseSystems && approvedMappings.length === 0}>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Schema
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Bottom navigation — always-available Back to Step 3.  Required for
            the empty-data-source dead-end where the user previously had no
            navigation out of Step 4 at all. */}
        <div className="flex justify-between pt-6">
          <Button variant="outline" onClick={onBack}>
            ← Back to Data Mapping
          </Button>
        </div>
      </div>
    );
  }

  // Schema generated - display results
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">
          Neo4j Schema Design
        </h2>
        <p className="text-neutral-600">
          Review your generated graph database schema
        </p>
      </div>

      {hasEnterpriseSystems && (
        <Button
          variant="outline"
          className="mb-4"
          onClick={() => setDesignMode(null)}
        >
          Back to Mode Selection
        </Button>
      )}

      {/* Schema Summary */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
        <CardBody>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <h3 className="font-semibold text-neutral-900">
                {hasSavedSchema ? `Schema saved (v ${savedSchemaVersion ?? 1})` : 'Schema generated — not yet saved'}
              </h3>
            </div>
            {savedSchemaVersion && (
              <Badge variant="success">Version {savedSchemaVersion}</Badge>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-2xl font-bold text-neutral-900">{schemaDesign.nodes.length}</p>
              <p className="text-sm text-neutral-600">Nodes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">{schemaDesign.relationships.length}</p>
              <p className="text-sm text-neutral-600">Relationships</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">{schemaDesign.constraints.length}</p>
              <p className="text-sm text-neutral-600">Constraints</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">{schemaDesign.indexes.length}</p>
              <p className="text-sm text-neutral-600">Indexes</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
          <Button
            variant={viewMode === 'visual' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('visual')}
          >
            <Eye className="w-4 h-4 mr-1" />
            Visual
          </Button>
          <Button
            variant={viewMode === 'details' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('details')}
          >
            <List className="w-4 h-4 mr-1" />
            Details
          </Button>
          <Button
            variant={viewMode === 'mapping' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('mapping')}
          >
            <ArrowRight className="w-4 h-4 mr-1" />
            Mapping
          </Button>
        </div>
        <Button
          variant={showCypher ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setShowCypher(!showCypher)}
        >
          <Code className="w-4 h-4 mr-2" />
          {showCypher ? 'Hide' : 'Show'} Cypher DDL
        </Button>
        {/* Regenerate re-runs whatever SOURCE this schema came from. When it came
            from an accepted suggested ontology, reopen the ontology view instead of
            the table-driven generate-stream — which would otherwise silently discard
            the ontology and rebuild from enterprise-system tables. */}
        {schemaFromSuggestion && SUGGESTED_SCHEMA_FLAG && caseId ? (
          <Button variant="outline" size="sm" onClick={() => setShowSuggestedQuick(true)}>
            <Sparkles className="w-4 h-4 mr-2" />
            Regenerate ontology
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={handleGenerateSchema}>
            <Sparkles className="w-4 h-4 mr-2" />
            Regenerate
          </Button>
        )}
      </div>

      {/* Schema Visualizer */}
      {viewMode === 'visual' && schemaDesign.nodes.length > 0 && (
        <Card>
          <CardBody className="p-0">
            <SchemaVisualizer
              schema={convertToNeo4jSchema(schemaDesign)}
              onSchemaUpdate={(updatedSchema) => {
                // Convert back to SchemaDesign format and update
                const updated: SchemaDesign = {
                  nodes: updatedSchema.nodes.map(n => ({
                    label: n.label,
                    properties: (n.properties || []).map(p => ({
                      name: p.name,
                      type: p.type,
                      required: p.required || false,
                    })),
                  })),
                  relationships: updatedSchema.relationships.map(r => ({
                    type: r.type,
                    from: r.sourceLabel || (r as any).from,
                    to: r.targetLabel || (r as any).to,
                    properties: (r.properties || []).map(p => ({
                      name: p.name,
                      type: p.type,
                    })),
                  })),
                  constraints: schemaDesign.constraints,
                  indexes: schemaDesign.indexes,
                  cypherDDL: schemaDesign.cypherDDL,
                };
                setSchemaDesign(updated);
              }}
            />
          </CardBody>
        </Card>
      )}

      {/* Cypher DDL — uses the shared SchemaDDLViewer that fetches the
          canonical backend DDL via GET /case-analysis/cases/{id}/schema/cypher-ddl.
          Replaces the previous in-component <pre> dump of schemaDesign.cypherDDL
          (which was derived from the local mock generator). The viewer
          handles syntax highlighting, Copy + Download buttons, and renders
          a no-schema-yet empty state when the backend hasn't persisted a
          schema for this case yet — at which point the user falls back to
          schemaDesign.cypherDDL via the pre block below. */}
      {showCypher && caseId && (
        <SchemaDDLViewer caseId={caseId} />
      )}
      {showCypher && !caseId && schemaDesign.cypherDDL && (
        <Card>
          <CardHeader>
            <h3 className="font-semibold text-neutral-900">Cypher DDL (preview)</h3>
          </CardHeader>
          <CardBody>
            <pre className="bg-neutral-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm font-mono">
              {schemaDesign.cypherDDL}
            </pre>
            <p className="text-xs text-neutral-500 mt-2">
              Backend DDL unavailable (no caseId in URL). Showing locally-generated preview only.
            </p>
          </CardBody>
        </Card>
      )}

      {/* Nodes and Relationships - only show in Details view */}
      {viewMode === 'details' && (
      <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-neutral-900">Node Types</h3>
            <Badge variant="secondary">{schemaDesign.nodes.length}</Badge>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-4">
            {schemaDesign.nodes.map((node, index) => (
              <div key={index} className="p-4 border-2 border-blue-200 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-neutral-900">:{node.label}</h4>
                  <Badge variant="info">{node.properties.length} properties</Badge>
                </div>
                <div className="space-y-2">
                  {node.properties.map((prop, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-neutral-900">{prop.name}</span>
                        <Badge variant="secondary" className="text-xs">{prop.type}</Badge>
                      </div>
                      <div className="flex gap-1">
                        {prop.required && <Badge variant="error" className="text-xs">Required</Badge>}
                        {prop.unique && <Badge variant="warning" className="text-xs"><Key className="w-3 h-3" /></Badge>}
                        {prop.indexed && <Badge variant="info" className="text-xs"><Search className="w-3 h-3" /></Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Relationships */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-neutral-900">Relationships</h3>
            <Badge variant="secondary">{schemaDesign.relationships.length}</Badge>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-3">
            {schemaDesign.relationships.map((rel, index) => (
              <div key={index} className="p-4 border-2 border-purple-200 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-semibold text-sm">
                    {rel.from}
                  </span>
                  <span className="text-neutral-400">-</span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded font-mono font-semibold text-sm">
                    [{rel.type}]
                  </span>
                  <span className="text-neutral-400">-</span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-semibold text-sm">
                    {rel.to}
                  </span>
                </div>
                {rel.properties && rel.properties.length > 0 && (
                  <div className="mt-2 pl-4 border-l-2 border-purple-300">
                    <p className="text-xs font-medium text-neutral-600 mb-1">Properties:</p>
                    <div className="flex gap-2 flex-wrap">
                      {rel.properties.map((prop, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-white rounded border border-purple-200">
                          {prop.name}: {prop.type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
      </>
      )}

      {/* Source-to-Target Mapping View */}
      {viewMode === 'mapping' && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-neutral-900">Source to Target Mapping</h3>
              <Badge variant="secondary">
                {systemDataSources.reduce((acc, sys) => acc + (sys.tables?.length || 0), 0)} tables →{' '}
                {schemaDesign.nodes.length} nodes
              </Badge>
            </div>
          </CardHeader>
          <CardBody>
            {systemDataSources.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <Table2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No source systems selected</p>
                <p className="text-sm">Select systems in Step 3 to see mappings</p>
              </div>
            ) : (
              <div className="space-y-6">
                {systemDataSources.map((system) => (
                  <div key={system.system_id} className="border rounded-lg overflow-hidden">
                    {/* System Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-3 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Database className="w-5 h-5 text-blue-600" />
                          <span className="font-semibold text-neutral-900">{system.system_name}</span>
                          <Badge variant="info">{system.tables?.length || 0} tables</Badge>
                        </div>
                        <span className="text-sm text-neutral-500">{system.vendor || 'Enterprise System'}</span>
                      </div>
                    </div>

                    {/* Table Mappings */}
                    <div className="divide-y">
                      {system.tables?.map((table) => {
                        // Find the corresponding node in schemaDesign
                        const targetNodeLabel = table.table_name
                          .split(/[_\s]+/)
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join('');
                        const targetNode = schemaDesign.nodes.find(
                          n => n.label === targetNodeLabel || n.label.toLowerCase() === table.table_name.toLowerCase()
                        );

                        // Get all columns from the table (handle categorized structure)
                        const allColumns = table.columns
                          ? [...(table.columns.must || []), ...(table.columns.should || []), ...(table.columns.could || [])]
                          : [];

                        return (
                          <div key={table.table_id} className="p-4">
                            <div className="flex items-start gap-4">
                              {/* Source (Table) */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <Table2 className="w-4 h-4 text-blue-500" />
                                  <span className="font-mono text-sm font-medium text-neutral-800">
                                    {table.table_name}
                                  </span>
                                  {allColumns.length > 0 && (
                                    <Badge variant="secondary" className="text-xs">
                                      {allColumns.length} cols
                                    </Badge>
                                  )}
                                </div>
                                {table.table_description && (
                                  <p className="text-xs text-neutral-500 mb-2 ml-6">{table.table_description}</p>
                                )}
                                {/* Source Columns */}
                                {allColumns.length > 0 && (
                                  <div className="ml-6 flex flex-wrap gap-1">
                                    {allColumns.slice(0, 8).map((col, idx) => (
                                      <span
                                        key={idx}
                                        className={`text-xs px-2 py-0.5 rounded ${
                                          col.is_primary_key
                                            ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                                            : col.is_foreign_key
                                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                            : 'bg-neutral-100 text-neutral-600'
                                        }`}
                                      >
                                        {col.column_name}
                                        {col.is_primary_key && <Key className="w-3 h-3 inline ml-1" />}
                                      </span>
                                    ))}
                                    {allColumns.length > 8 && (
                                      <span className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-500 rounded">
                                        +{allColumns.length - 8} more
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Arrow */}
                              <div className="flex items-center py-4">
                                <ArrowRight className="w-6 h-6 text-purple-400" />
                              </div>

                              {/* Target (Node) */}
                              <div className="flex-1 min-w-0">
                                {targetNode ? (
                                  <>
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="w-4 h-4 rounded-full bg-green-500" />
                                      <span className="font-mono text-sm font-medium text-green-700">
                                        :{targetNode.label}
                                      </span>
                                      <Badge variant="success" className="text-xs">
                                        {targetNode.properties.length} props
                                      </Badge>
                                    </div>
                                    {/* Target Properties */}
                                    <div className="ml-6 flex flex-wrap gap-1">
                                      {targetNode.properties.slice(0, 8).map((prop, idx) => (
                                        <span
                                          key={idx}
                                          className={`text-xs px-2 py-0.5 rounded ${
                                            prop.required
                                              ? 'bg-green-100 text-green-700 border border-green-300'
                                              : 'bg-neutral-100 text-neutral-600'
                                          }`}
                                        >
                                          {prop.name}
                                          <span className="text-neutral-400 ml-1">:{prop.type}</span>
                                        </span>
                                      ))}
                                      {targetNode.properties.length > 8 && (
                                        <span className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-500 rounded">
                                          +{targetNode.properties.length - 8} more
                                        </span>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex items-center gap-2 text-neutral-400">
                                    <div className="w-4 h-4 rounded-full border-2 border-dashed border-neutral-300" />
                                    <span className="text-sm italic">No matching node</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Summary */}
                <div className="bg-neutral-50 rounded-lg p-4 border">
                  <h4 className="font-medium text-neutral-900 mb-3">Mapping Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{systemDataSources.length}</div>
                      <div className="text-xs text-neutral-500">Source Systems</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        {systemDataSources.reduce((acc, sys) => acc + (sys.tables?.length || 0), 0)}
                      </div>
                      <div className="text-xs text-neutral-500">Source Tables</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{schemaDesign.nodes.length}</div>
                      <div className="text-xs text-neutral-500">Target Nodes</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">{schemaDesign.relationships.length}</div>
                      <div className="text-xs text-neutral-500">Relationships</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext}>
          Continue to Review
        </Button>
      </div>
    </div>
  );
};

export default Step4SchemaPreviewEnhanced;
